import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import usePermissions from "datasets/hooks/usePermissions";
import useDatasetReportFQNs from "datasets/pages/dashboards/hooks/useDatasetReportFQNs";
import useDatasetReports from "datasets/pages/dashboards/hooks/useDatasetReports";
import useReport from "datasets/pages/dashboards/hooks/useReport";
import queryString from "query-string";
import yn from "yn";
import Loading from "~/components/Loading";
import SigmaEmbed from "~/components/SigmaEmbed";
import useErrorToast from "~/contexts/error-toast";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_SIGMA_SELF_SERVICE_ENABLED } = import.meta.env;

/**
 * Modifies the source URL based on user permissions
 * - If user has edit:catalog-report permission, adds /edit and necessary query parameters
 * - Otherwise returns the original URL
 */
const getModifiedSourceUrl = (sourceUrl: string, hasEditPermission: boolean): string => {
  if (!yn(VITE_SIGMA_SELF_SERVICE_ENABLED) || !hasEditPermission) {
    return sourceUrl;
  }

  // Parse the URL to handle existing query parameters and check if path already ends with /edit
  const url = new URL(sourceUrl);

  // Check if pathname already ends with /edit
  if (!url.pathname.endsWith("/edit")) {
    url.pathname = `${url.pathname}/edit`;
  }

  // Add editing query parameters to any existing ones
  const newParams = {
    [":hide_menu"]: true,
    [":hide_folder_navigation"]: true,
    [":menu_position"]: "bottom",
  };

  // Parse existing query string
  const existingParams = queryString.parse(url.search);

  // Combine existing and new parameters
  const combinedParams = { ...existingParams, ...newParams };

  // Set the new search string
  url.search = queryString.stringify(combinedParams);

  return url.toString();
};

const DatasetReport = () => {
  const { datasetId, reportId } = useParams();
  const { data: report } = useReport();
  const { mutate: mutateDataset } = useDataset();
  const { mutate: mutateReports } = useDatasetReports();
  const { showError } = useErrorToast();
  const { data: reportFQNs, mutate: mutateReportFQNs } = useDatasetReportFQNs();
  const navigate = useNavigate();
  const hasEditPermission = usePermissions("edit:catalog-report");

  const handleClose = () => navigate("..", { relative: "path" });

  const handleRemove = async () => {
    if (report && reportFQNs) {
      const filteredReports = reportFQNs?.filter((fqn) => fqn !== report.fullyQualifiedName);

      try {
        const resp = await fetch(`${VITE_CATALOG_API}/datasets/${datasetId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reports: filteredReports,
          }),
        });

        if (!resp.ok) {
          throw Error("There was a problem trying to remove the report");
        }

        await mutateDataset();
        await mutateReports();
        await mutateReportFQNs();
        handleClose();
      } catch (e: any) {
        console.error(e);
        rumClient?.recordError(e);
        showError(e.message);
      }
    }
  };

  const messageHandler = async (e: MessageEvent) => {
    if ("workbook:ondelete" === e.data.type) {
      await handleRemove();
    }
  };

  useEffect(() => {
    trackEvent("dataset_dashboard_viewed", {
      dataset_id: datasetId,
      dashboard_id: reportId,
    });
  }, [datasetId, reportId]);

  if (!report) {
    return <Loading />;
  }

  return (
    <SigmaEmbed
      title={report.displayName}
      workbookUrl={getModifiedSourceUrl(report.sourceUrl, hasEditPermission)}
      messageHandler={messageHandler}
      onClose={handleClose}
    />
  );
};

export default DatasetReport;
